import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ProductCard } from '../components/ProductCard';
import { Filter, ChevronDown, Check, X, Search, SlidersHorizontal } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { SEO } from '../components/SEO';
import { sortProducts, SORT_OPTIONS, isValidSortKey, SortKey } from '../utils/sortProducts';
export function Shop() {
  const {
    products
  } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeGroup, setActiveGroup] = useState<string>('All');
  const [activeTask, setActiveTask] = useState<string>('All');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const sortParam = searchParams.get('sort');
  const sortBy: SortKey = isValidSortKey(sortParam) ? sortParam : 'price-desc';
  const updateSort = (next: SortKey) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', next);
    setSearchParams(params);
  };
  const groups = ['All', 'Complete Systems', 'Bundles', 'Modules', 'Accessories'];
  const tasks = ['All', 'Lawn Care', 'Snow Removal', 'Leaf Cleanup', 'Trimming & Edging', 'Tow & Transport'];
  const categories = ['All', 'Robot Lawn Mowers', 'Robotic Snow Blowers', 'Leaf Blowers', 'Modules & Accessories'];
  const filteredProducts = useMemo(() => {
    // 1. Filter
    const filtered = products.filter(p => {
      if (searchParams.get('search')) {
        const q = searchParams.get('search')?.toLowerCase() || '';
        const matchesSearch = p.name.toLowerCase().includes(q) || p.sku && p.sku.toLowerCase().includes(q) || p.shortDescription.toLowerCase().includes(q) || p.publicCategories && p.publicCategories.some(c => c.toLowerCase().includes(q)) || p.taskCategories && p.taskCategories.some(c => c.toLowerCase().includes(q)) || p.category.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      if (activeGroup !== 'All') {
        if (p.primaryGroup !== activeGroup && p.category !== activeGroup) return false;
      }
      if (activeTask !== 'All') {
        if (!p.taskCategories?.includes(activeTask)) return false;
      }
      if (activeCategory !== 'All') {
        if (activeCategory === 'Modules & Accessories') {
          if (p.category !== 'Modules' && p.category !== 'Accessories' && !p.publicCategories?.includes('Modules & Accessories')) return false;
        } else {
          if (!p.publicCategories?.includes(activeCategory)) return false;
        }
      }
      return true;
    });
    // 2. Sort via centralized helper
    return sortProducts(filtered, sortBy);
  }, [searchParams.get('search'), activeGroup, activeTask, activeCategory, sortBy, products]);
  const clearSearch = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params);
  };
  const resetFilters = () => {
    setActiveGroup('All');
    setActiveTask('All');
    setActiveCategory('All');
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1837">
      <SEO title="Shop Yarbo Products" description="Browse our selection of Yarbo robotic yard care systems, modules, and accessories." data-id="element-1838" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8" data-id="element-1839">
        <div data-id="element-1840">
          <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-2" data-id="element-1841">
            Shop Yarbo Products
          </h1>
          <p className="text-gray-600 max-w-2xl" data-id="element-1842">
            Complete systems, multi-module bundles, individual modules, and
            accessories. Online ordering with nationwide shipping.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-4" data-id="element-1843">
          <div className="flex-1" data-id="element-1844">
            <div className="relative" data-id="element-1845">
              <Search className="absolute left-3 top-1/2 h-5 w-5 text-gray-400 transform -translate-y-1/2" data-id="element-1846" />
              <input type="text" placeholder="Search products..." value={searchParams.get('search') || ''} onChange={e => {
              const params = new URLSearchParams(searchParams);
              if (e.target.value) params.set('search', e.target.value);else params.delete('search');
              setSearchParams(params);
            }} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-1847" />
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2" data-id="element-1848">
            <select value={sortBy} onChange={e => updateSort(e.target.value as SortKey)} className="text-sm font-semibold border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-1849">
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-1850">
                  {opt.label}
                </option>)}
            </select>
            <button onClick={resetFilters} className="text-sm font-semibold text-gray-500 hover:text-yarbo-dark border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50" data-id="element-1851">
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8" data-id="element-1852">
        {/* Mobile filter toggle */}
        <div className="md:hidden" data-id="element-1853">
          <button onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)} className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between font-bold text-sm" data-id="element-1854">
            <span className="flex items-center gap-2" data-id="element-1855">
              <SlidersHorizontal className="w-4 h-4" data-id="element-1856" /> Filters & Sort
            </span>
            {isMobileFilterOpen ? <X className="w-4 h-4" data-id="element-1857" /> : <Filter className="w-4 h-4" data-id="element-1858" />}
          </button>
        </div>

        {/* Sidebar */}
        <aside className={`md:w-60 flex-shrink-0 ${isMobileFilterOpen ? 'block' : 'hidden md:block'}`} data-id="element-1859">
          <div className="bg-white p-5 rounded-xl border border-gray-200 sticky top-24 space-y-6" data-id="element-1860">
            <div data-id="element-1861">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3" data-id="element-1862">
                Product Group
              </h3>
              <div className="space-y-1" data-id="element-1863">
                {groups.map(g => <button key={g} onClick={() => setActiveGroup(g)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeGroup === g ? 'bg-yarbo-dark text-white font-bold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1864">
                    {g}
                  </button>)}
              </div>
            </div>

            <div data-id="element-1865">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3" data-id="element-1866">
                Yard Task
              </h3>
              <div className="space-y-1" data-id="element-1867">
                {tasks.map(t => <button key={t} onClick={() => setActiveTask(t)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeTask === t ? 'bg-yarbo-dark text-white font-bold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1868">
                    {t}
                  </button>)}
              </div>
            </div>

            <div data-id="element-1869">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3" data-id="element-1870">
                Category
              </h3>
              <div className="space-y-1" data-id="element-1871">
                {categories.map(c => <button key={c} onClick={() => setActiveCategory(c)} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${activeCategory === c ? 'bg-yarbo-dark text-white font-bold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1872">
                    {c}
                  </button>)}
              </div>
            </div>

            <button onClick={resetFilters} className="w-full text-xs font-semibold text-gray-500 hover:text-yarbo-dark border-t border-gray-100 pt-4" data-id="element-1873">
              Reset all filters
            </button>
          </div>
        </aside>

        {/* Grid */}
        <div className="flex-grow" data-id="element-1874">
          <div className="flex justify-between items-center mb-5" data-id="element-1875">
            <span className="text-sm text-gray-600 font-medium" data-id="element-1876">
              Showing{' '}
              <span className="font-bold text-yarbo-dark" data-id="element-1877">
                {filteredProducts.length}
              </span>{' '}
              products
            </span>
            <select value={sortBy} onChange={e => updateSort(e.target.value as SortKey)} className="text-sm font-semibold border border-gray-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-1878">
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-1879">
                  {opt.label}
                </option>)}
            </select>
          </div>

          {filteredProducts.length > 0 ? <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-1880">
              {filteredProducts.map((product, idx) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: Math.min(idx * 0.03, 0.4)
          }} data-id="element-1881">
                  <ProductCard product={product} data-id="element-1882" />
                </motion.div>)}
            </div> : <div className="bg-white p-12 rounded-xl border border-gray-200 text-center" data-id="element-1883">
              <h3 className="text-lg font-bold text-yarbo-dark mb-2" data-id="element-1884">
                No products match
              </h3>
              <button onClick={() => {
            resetFilters();
            if (searchParams.get('search')) clearSearch();
          }} className="text-sm font-bold text-yarbo-dark hover:underline" data-id="element-1885">
                Clear filters
              </button>
            </div>}
        </div>
      </div>
    </div>;
}